/**
 * Подписки и значки сервисов (04.11.2026).
 *
 * Подписку узнают по значку быстрее, чем по названию — как карту по логотипу
 * банка (`banks.ts`). Значки взяты с официальных сайтов сервисов (значок
 * сайта) и лежат в `assets/services/`: это картинки, а не Font Awesome, и
 * рисуются `<img>` на белой плашке. Сервис без значка — кружок с первой
 * буквой.
 *
 * Отдельного вида «подписка» человек не выбирает: платёж — подписка, если он
 * так заведён (`kind: 'subscription'`) или по названию узнаётся сервис.
 */
import type { Obligation } from '@skoka/core';
import amediateka from './assets/services/amediateka.png';
import chatgpt from './assets/services/chatgpt.png';
import claude from './assets/services/claude.png';
import icloud from './assets/services/icloud.png';
import ivi from './assets/services/ivi.png';
import kinopoisk from './assets/services/kinopoisk.png';
import kion from './assets/services/kion.png';
import litres from './assets/services/litres.png';
import mtspremium from './assets/services/mtspremium.png';
import okko from './assets/services/okko.png';
import premier from './assets/services/premier.png';
import start from './assets/services/start.png';
import telegram from './assets/services/telegram.png';
import vkmusic from './assets/services/vkmusic.png';
import ybooks from './assets/services/ybooks.png';
import yplus from './assets/services/yplus.png';
import youtube from './assets/services/youtube.png';

/** Значок самого приложения лежит в `public/`. */
const skoka = '/app-icon-192.png';

export interface Service { id: string; name: string; src: string; match: RegExp }

/** Порядок важен: «Яндекс Книги» проверяются раньше «Яндекс Плюса». */
export const SERVICES: readonly Service[] = [
  { id: 'skoka', name: 'SKOKA Pro', src: skoka, match: /skoka|скока/i },
  { id: 'ybooks', name: 'Яндекс Книги', src: ybooks, match: /яндекс\s*книг|букмейт|bookmate/i },
  { id: 'yplus', name: 'Яндекс Плюс', src: yplus, match: /плюс|yandex\s*plus/i },
  { id: 'kinopoisk', name: 'Кинопоиск', src: kinopoisk, match: /кинопоиск|kinopoisk/i },
  { id: 'ivi', name: 'Иви', src: ivi, match: /\bivi\b|\bиви\b/i },
  { id: 'okko', name: 'Okko', src: okko, match: /okko|окко/i },
  { id: 'kion', name: 'Кион', src: kion, match: /kion|кион/i },
  { id: 'premier', name: 'Premier', src: premier, match: /premier|премьер/i },
  { id: 'start', name: 'Start', src: start, match: /^start\b|^старт\b/i },
  { id: 'amediateka', name: 'Амедиатека', src: amediateka, match: /амедиатек|amediateka/i },
  { id: 'vkmusic', name: 'VK Музыка', src: vkmusic, match: /vk\s*муз|вк\s*муз|boom/i },
  { id: 'mtspremium', name: 'МТС Premium', src: mtspremium, match: /мтс\s*(premium|премиум)/i },
  { id: 'litres', name: 'Литрес', src: litres, match: /литрес|litres/i },
  { id: 'telegram', name: 'Telegram Premium', src: telegram, match: /telegram|телеграм/i },
  { id: 'icloud', name: 'iCloud', src: icloud, match: /icloud|айклауд/i },
  { id: 'chatgpt', name: 'ChatGPT', src: chatgpt, match: /chat\s*gpt|openai|\bgpt\b/i },
  { id: 'claude', name: 'Claude', src: claude, match: /claude|клод/i },
  { id: 'youtube', name: 'YouTube Premium', src: youtube, match: /youtube|ютуб/i },
];

export const serviceOf = (name: string): Service | null =>
  SERVICES.find((s) => s.match.test(name.trim())) ?? null;

/** Платёж — подписка: заведён так или по названию узнаётся сервис. */
export const isSubscription = (o: Pick<Obligation, 'kind' | 'name'>): boolean =>
  o.kind === 'subscription' || serviceOf(o.name) !== null || /подписк/i.test(o.name);
